import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, InstagramIcon, PlayIcon } from 'lucide-react';
import { SectionLabel } from './SectionLabel';
const INSTAGRAM_URL = 'https://www.instagram.com/vivomedia.uy?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw==';
const POSTS = ['https://cdn.magicpatterns.com/patterns/generated-images/151bbf66-e948-4917-8b2a-7b8d15379d1d.jpg', 'https://cdn.magicpatterns.com/patterns/generated-images/1b0ca2c3-aea5-4a27-bd89-46874eeb4934.jpg', 'https://cdn.magicpatterns.com/patterns/generated-images/151bbf66-e948-4917-8b2a-7b8d15379d1d.jpg', 'https://cdn.magicpatterns.com/patterns/generated-images/1b0ca2c3-aea5-4a27-bd89-46874eeb4934.jpg', 'https://cdn.magicpatterns.com/patterns/generated-images/151bbf66-e948-4917-8b2a-7b8d15379d1d.jpg', 'https://cdn.magicpatterns.com/patterns/generated-images/1b0ca2c3-aea5-4a27-bd89-46874eeb4934.jpg'];
export function InstagramFeed() {
  const carouselRef = useRef<HTMLDivElement>(null);
  const [canGoBack, setCanGoBack] = useState(false);
  const [canGoForward, setCanGoForward] = useState(true);
  const updateControls = () => {
    const carousel = carouselRef.current;
    if (!carousel) return;
    setCanGoBack(carousel.scrollLeft > 4);
    setCanGoForward(carousel.scrollLeft < carousel.scrollWidth - carousel.clientWidth - 4);
  };
  useEffect(() => {
    updateControls();
    window.addEventListener('resize', updateControls);
    return () => window.removeEventListener('resize', updateControls);
  }, []);
  const moveCarousel = (direction: -1 | 1) => {
    const carousel = carouselRef.current;
    if (!carousel) return;
    carousel.scrollBy({
      left: direction * carousel.clientWidth * 0.82,
      behavior: 'smooth'
    });
    window.setTimeout(updateControls, 350);
  };
  const handlePostClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    const carousel = carouselRef.current;
    if (!carousel || !canGoForward) return;
    const carouselBounds = carousel.getBoundingClientRect();
    const postBounds = event.currentTarget.getBoundingClientRect();
    const isLastVisiblePost = postBounds.right >= carouselBounds.right - 12;
    if (isLastVisiblePost) {
      event.preventDefault();
      moveCarousel(1);
    }
  };
  return <section id="instagram" className="w-full bg-vm-bg py-28 lg:py-36" data-id="element-120" data-mp="3p38k1eywggwj">
      <div className="mx-auto max-w-7xl px-6 lg:px-10" data-id="element-121" data-mp="pnbw3cu2woft">
        <div className="flex items-end justify-between gap-6" data-id="element-122" data-mp="pmsfbzg6063d">
          <div className="max-w-4xl" data-id="element-123" data-mp="ll792wy4yq25">
            <SectionLabel tone="cream" data-id="element-124" data-mp="lm8hsuroj0gp">Instagram</SectionLabel>
          </div>

          <div className="hidden items-center gap-3 sm:flex" aria-label="Controles del carrusel de Instagram" data-id="element-125" data-mp="glkv2lahh5sa">
            <CarouselButton direction="back" disabled={!canGoBack} onClick={() => moveCarousel(-1)} data-id="element-126" data-mp="bn15mawolbv6" />
            <CarouselButton direction="forward" disabled={!canGoForward} onClick={() => moveCarousel(1)} data-id="element-127" data-mp="31efx28099je2" />
          </div>
        </div>

        <motion.div ref={carouselRef} initial={{
        opacity: 0,
        y: 18
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-40px'
      }} transition={{
        duration: 0.5
      }} onScroll={updateControls} className="mt-8 flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth pb-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" aria-label="Publicaciones de Instagram" data-id="element-128" data-mp="2m9uzjla4t1ig">
          {POSTS.map((src, i) => <a key={`${src}-${i}`} href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" onClick={handlePostClick} className="group relative aspect-square w-[82%] shrink-0 snap-start overflow-hidden rounded-2xl sm:w-[42%] lg:w-[31.8%]" data-id="element-129" data-mp="39z5rcd9i7lkg" data-mpi={i}>
              <img src={src} alt={`Publicación de Instagram de Vivo Media ${i + 1}`} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-130" data-mp="1klst95rzitrs" />
              <div className="absolute inset-0 bg-vm-navy/55 transition-colors group-hover:bg-vm-navy/25" data-id="element-131" data-mp="3b1lebe22jmm2" />
              <div className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity group-hover:opacity-100" data-id="element-132" data-mp="3p149755vd1gf">
                <PlayIcon className="h-10 w-10 text-white" aria-hidden="true" data-id="element-133" data-mp="3k3cey4hr5392" />
              </div>
            </a>)}
        </motion.div>

        <div className="mt-6 flex items-center justify-between gap-5 sm:mt-12 sm:justify-center" data-id="element-134" data-mp="2wace6b0jiiuo">
          <div className="flex items-center gap-3 sm:hidden" aria-label="Controles del carrusel de Instagram" data-id="element-135" data-mp="2s9afpweg6atr">
            <CarouselButton direction="back" disabled={!canGoBack} onClick={() => moveCarousel(-1)} data-id="element-136" data-mp="31brzm46mqrdl" />
            <CarouselButton direction="forward" disabled={!canGoForward} onClick={() => moveCarousel(1)} data-id="element-137" data-mp="2dlxpvvhlxixk" />
          </div>

          <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-2xl border-2 border-vm-electric px-6 py-3 text-xs font-semibold uppercase tracking-[0.1em] text-white transition-colors hover:bg-vm-electric sm:px-8 sm:py-4 sm:text-sm" data-id="element-138" data-mp="28nw7lcs09nn5">
            <InstagramIcon className="h-5 w-5" aria-hidden="true" data-id="element-139" data-mp="f2kibjhna6hs" />
            Ver más en Instagram
          </a>
        </div>
      </div>
    </section>;
}
interface CarouselButtonProps {
  direction: 'back' | 'forward';
  disabled: boolean;
  onClick: () => void;
}
function CarouselButton({
  direction,
  disabled,
  onClick
}: CarouselButtonProps) {
  const isBack = direction === 'back';
  const Icon = isBack ? ChevronLeftIcon : ChevronRightIcon;
  return <button type="button" onClick={onClick} disabled={disabled} aria-label={isBack ? 'Ver publicaciones anteriores' : 'Ver más publicaciones'} className="flex h-11 w-11 items-center justify-center rounded-full border border-vm-muted/60 text-white transition-colors hover:border-vm-cream hover:bg-vm-cream hover:text-vm-bg focus:outline-none focus-visible:ring-2 focus-visible:ring-vm-cream disabled:cursor-not-allowed disabled:opacity-35" data-id="element-140" data-mp="12son3u6kkcad">
      <Icon className="h-5 w-5" aria-hidden="true" data-id="element-141" data-mp="2up97f9lwl7vx" />
    </button>;
}